import { Box, Container, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RequestCodeForCSRequest } from 'apis/auth/types';
import { useRequestCodeForCS } from 'apis/auth/hook';
import Input from 'components/Common/Input';
import Button from 'components/Common/Button';
import { ssoSchema } from './ValidationSchema';
import { getCookie } from 'utils/helperFunctions';
import { CacheKeys } from 'utils/cacheKeys';
import { useSearchParams } from 'react-router-dom';
import KickCallSupportLogo from '@/assets/kickcallSupport.svg';
import KickCallLogo from '@/assets/KickCallWhiteLogo.svg';

export default function SSO() {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to');

  const ssoSupportToken = getCookie(CacheKeys.ssoSupportToken);
  const { mutate: requestCodeForCS, isPending } = useRequestCodeForCS();

  const { control, handleSubmit } = useForm<RequestCodeForCSRequest>({
    resolver: zodResolver(ssoSchema()),
    mode: 'onBlur'
  });

  const onSubmit = (data: RequestCodeForCSRequest) => {
    requestCodeForCS(data);
  };

  if (returnTo && ssoSupportToken !== undefined) {
    const redirectUrl = new URL('https://supportkickcall.featurebase.app/api/v1/auth/access/jwt');
    redirectUrl.searchParams.set('jwt', ssoSupportToken as string);
    redirectUrl.searchParams.set('return_to', returnTo);
    window.location.href = redirectUrl.toString();
    return;
  }

  return (
    <Container
      maxWidth='xs'
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        py: 2
      }}
    >
      {/* Centered content */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <img src={KickCallSupportLogo} alt='app-logo' width={90} />
          <Typography variant='h1' fontSize={24} fontWeight={500} mt={2} color='#09090A'>
            Log-In to Kickcall Support
          </Typography>
        </Box>

        <Box
          component='form'
          sx={{ width: '100%', maxWidth: '100%' }}
          noValidate
          autoComplete='off'
          onSubmit={handleSubmit(onSubmit)}
        >
          <Input name='agency_code' label='Agency Code' type='number' margin='normal' control={control} fullWidth />
          <Input
            name='email'
            label='Email address'
            type='email'
            margin='normal'
            control={control}
            fullWidth
            autoComplete='email'
          />
          <Button type='submit' variant='contained' color='primary' fullWidth sx={{ mt: 3 }} isPending={isPending}>
            Continue
          </Button>
        </Box>
      </Box>

      {/* Fixed bottom section */}
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          pb: 2
        }}
      >
        <Typography variant='h1' fontSize={16} color='#777'>
          Powered By
        </Typography>
        <img src={KickCallLogo} alt='kickcall-logo' width={48} />
      </Box>
    </Container>
  );
}
